import SharedInfoPanel from "../../shared/InfoPanel"
import "../styles/InfoPanel.css"

const TABS = [
  { label: "Overview", key: "info" },
  { label: "Algorithm", key: "algorithm" },
  { label: "Applications", key: "applications" },
]

const STACK_INFO = {
  description:
    "A stack is a linear data structure that follows the Last In, First Out (LIFO) principle. Elements are added and removed from the same end, called the 'top' of the stack.",
  operations: [
    { name: "Push", description: "Adds an element to the top of the stack", complexity: "O(1)" },
    { name: "Pop", description: "Removes the top element from the stack", complexity: "O(1)" },
    { name: "Peek/Top", description: "Returns the top element without removing it", complexity: "O(1)" },
    { name: "isEmpty", description: "Checks if the stack is empty", complexity: "O(1)" },
  ],
  applications: [
    "Function call management (Call Stack)",
    "Expression evaluation and syntax parsing",
    "Undo/Redo operations in applications",
    "Backtracking algorithms",
  ],
  pseudocode: {
    push: `function push(stack, element):
  if stack is full:
    return overflow error
  increment top
  stack[top] = element`,
    pop: `function pop(stack):
  if stack is empty:
    return underflow error
  element = stack[top]
  decrement top
  return element`,
  },
}

const QUEUE_INFO = {
  description:
    "A queue is a linear data structure that follows the First In, First Out (FIFO) principle. Elements are added at the rear and removed from the front of the queue.",
  operations: [
    { name: "Enqueue", description: "Adds an element to the rear of the queue", complexity: "O(1)" },
    { name: "Dequeue", description: "Removes an element from the front of the queue", complexity: "O(1)" },
    { name: "Front", description: "Returns the front element without removing it", complexity: "O(1)" },
    { name: "isEmpty", description: "Checks if the queue is empty", complexity: "O(1)" },
  ],
  applications: [
    "CPU scheduling in operating systems",
    "Handling of requests on a single shared resource",
    "Breadth-First Search algorithm",
    "Print queue management",
  ],
  pseudocode: {
    enqueue: `function enqueue(queue, element):
  if queue is full:
    return overflow error
  increment rear
  queue[rear] = element`,
    dequeue: `function dequeue(queue):
  if queue is empty:
    return underflow error
  element = queue[front]
  increment front
  return element`,
  },
}

function InfoPanel({ type }) {
  const isStack = type === "stack"
  const info = isStack ? STACK_INFO : QUEUE_INFO

  const renderTab = (activeTab) => {
    if (activeTab === "info") {
      return (
        <div className="info-section">
          <h3>What is a {isStack ? "Stack" : "Queue"}?</h3>
          <p>{info.description}</p>
          <div className="visual-explanation">
            {isStack ? (
              <div className="stack-diagram">
                <div className="diagram-item top">Top</div>
                <div className="diagram-item">Element 3</div>
                <div className="diagram-item">Element 2</div>
                <div className="diagram-item">Element 1</div>
                <div className="diagram-arrow push">Push</div>
                <div className="diagram-arrow pop">Pop</div>
              </div>
            ) : (
              <div className="queue-diagram">
                <div className="diagram-item front">Front</div>
                <div className="diagram-item">Element 1</div>
                <div className="diagram-item">Element 2</div>
                <div className="diagram-item">Element 3</div>
                <div className="diagram-item rear">Rear</div>
                <div className="diagram-arrow dequeue">Dequeue</div>
                <div className="diagram-arrow enqueue">Enqueue</div>
              </div>
            )}
          </div>
        </div>
      )
    }

    if (activeTab === "algorithm") {
      return (
        <div className="algorithm-section">
          <h3>Operations</h3>
          <table className="operations-table">
            <thead>
              <tr>
                <th>Operation</th>
                <th>Description</th>
                <th>Time Complexity</th>
              </tr>
            </thead>
            <tbody>
              {info.operations.map((op, index) => (
                <tr key={index}>
                  <td>{op.name}</td>
                  <td>{op.description}</td>
                  <td>{op.complexity}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h3>Pseudocode</h3>
          <div className="pseudocode">
            <h4>{isStack ? "Push Operation" : "Enqueue Operation"}</h4>
            <pre>{isStack ? info.pseudocode.push : info.pseudocode.enqueue}</pre>
            <h4>{isStack ? "Pop Operation" : "Dequeue Operation"}</h4>
            <pre>{isStack ? info.pseudocode.pop : info.pseudocode.dequeue}</pre>
          </div>
        </div>
      )
    }

    if (activeTab === "applications") {
      return (
        <div className="applications-section">
          <h3>Real-world Applications</h3>
          <ul className="applications-list">
            {info.applications.map((app, index) => (
              <li key={index} className="application-item">
                <div className="application-icon"></div>
                <div className="application-text">{app}</div>
              </li>
            ))}
          </ul>
          <div className="application-example">
            <h4>Example: {isStack ? "Browser History" : "Print Queue"}</h4>
            <p>
              {isStack
                ? "When you browse the internet, the back button uses a stack to keep track of the pages you've visited. Each new page is pushed onto the stack, and pressing back pops the current page off to return to the previous one."
                : "When multiple documents are sent to a printer, they're processed in the order they were received. The first document in the queue is the first one to be printed."}
            </p>
          </div>
        </div>
      )
    }
  }

  return (
    <SharedInfoPanel
      title={`${isStack ? "Stack" : "Queue"} Information`}
      tabs={TABS}
      renderTab={renderTab}
    />
  )
}

export default InfoPanel
